import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { Card, Button, ProgressBar, Alert, Modal, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Quiz,
  GeniusPlayStart,
  GeniusPanelItem,
  QuizAttemptResponse,
  UnlockedAchievementPayload,
  UnlockedCharacterPayload,
  LoginStreakHome,
  authService,
  quizService,
  pvpService,
  type HostedOpenAsyncPvPSummary,
  GeniusFinishAudit,
} from '../services/api';
import toast from 'react-hot-toast';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isPwaStandalone } from '../utils/pwaStandalone';
import SoloQuizResultsModal from './SoloQuizResultsModal';
import SoloQuizQuitConfirmModal from './SoloQuizQuitConfirmModal';
import { quizQuestionImageUrlsFromKey } from '../utils/quizQuestionImageSrc';
import { playGeniusSoundFromKey } from '../utils/quizGeniusSoundSrc';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import QuizPlayPageHeading from './QuizPlayPageHeading';
import {
  GENIUS_TIME_LIMIT_SECONDS,
  GENIUS_TOTAL_ROUNDS,
  geniusPointsForRound,
} from '../utils/geniusPlayScoring';
import { useQuizTimer } from '../hooks/useQuizTimer';
import { geniusPanelSlotClass } from '../utils/geniusPanelColors';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import { getQuizSfxEnabled, playQuizSfxCorrect, playQuizSfxIncorrect, setQuizSfxEnabledLocal } from '../audio/quizSfx';
import { useQuizSoundPreferenceOptional } from '../contexts/QuizSoundPreferenceContext';
import { useTranslation } from 'react-i18next';
import {
  buildSoloArenaHostCompleteBody,
  type SoloArenaPublishSnapshot,
  type SoloArenaResultLine,
} from '../utils/soloArenaPublishLayout';
import { normalizePlayDifficultyLabel } from '../utils/quizPlayBandScoring';
import { useGeniusWasm } from '../hooks/useGeniusWasm';

import './shared/QuizStyles.css';
import './QuizPlayer.css';
import './GeniusQuizPlayer.css';

const DEMO_GAP_MS = 140;
/** Deve ser >= transição CSS de saída do tile (--playing). */
const GENIUS_TILE_EFFECT_OUT_MS = 80;
const GENIUS_START_COUNTDOWN_SEC = 3;
const GENIUS_WRONG_RESULT_DELAY_MS = 2000;

function delayMs(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForTileEffectOut(): Promise<void> {
  await delayMs(GENIUS_TILE_EFFECT_OUT_MS);
}

type GeniusPhase = 'countdown' | 'demo' | 'input' | 'submitting';

export type GeniusPvpFinishPayload = {
  score: number;
  correctAnswers: number;
  panelQuestionIds: string[];
  geniusSequenceQuestionIds: string[];
  responseTimeSecondsPerQuestion: number[];
  correctPerQuestion: boolean[];
  durationSeconds: number;
  geniusFinishAudit?: GeniusFinishAudit;
  geniusResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
};

export interface GeniusQuizPlayerProps {
  quizId: string;
  quiz: Quiz;
  genius: GeniusPlayStart;
  startTime: Date;
  onPlayAgain: () => void | Promise<void>;
  playAgainLoading?: boolean;
  suppressResultsModal?: boolean;
  /** Async PvP host or opponent — skips solo save; calls onComplete instead. */
  pvpAsync?: {
    flow: 'host' | 'opponent';
    roomId: string;
    onComplete: (payload: GeniusPvpFinishPayload) => Promise<void>;
  };
  arenaPublishSnapshotRef?: React.MutableRefObject<SoloArenaPublishSnapshot | null>;
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  arenaPublishCheckedRef?: React.MutableRefObject<boolean>;
  /** Solo: arena publish + navigation (from QuizPlayer). */
  onExitHome?: () => void | Promise<void>;
  /** When true, parent provides `quiz-page` / `quiz-container` (PvP layout). */
  embeddedInPvp?: boolean;
  /** PvP quit confirmation (from PvPQuiz); skips solo quit modal. */
  onPvpQuitRequest?: () => void;
}

function normalizeGeniusStart(raw: GeniusPlayStart): GeniusPlayStart {
  const panel = (raw.panel ?? []).map((p) => ({
    questionId: String(p.questionId ?? (p as { QuestionId?: string }).QuestionId ?? ''),
    imageAssetKey: String(p.imageAssetKey ?? (p as { ImageAssetKey?: string }).ImageAssetKey ?? ''),
    soundAssetKey: String(p.soundAssetKey ?? (p as { SoundAssetKey?: string }).SoundAssetKey ?? ''),
    label: (p.label ?? (p as { Label?: string }).Label ?? null) as string | null,
  }));
  const roundsRaw = raw.rounds ?? (raw as { Rounds?: string[][] }).Rounds ?? [];
  const rounds = roundsRaw.map((r) => r.map((id) => String(id)));
  return {
    playSessionId: String(raw.playSessionId ?? (raw as { PlaySessionId?: string }).PlaySessionId ?? ''),
    panel,
    rounds,
    totalRounds: raw.totalRounds ?? (raw as { TotalRounds?: number }).TotalRounds ?? GENIUS_TOTAL_ROUNDS,
    engineBootstrapCipher: raw.engineBootstrapCipher ?? (raw as { EngineBootstrapCipher?: string }).EngineBootstrapCipher ?? null,
  };
}

const GeniusQuizPlayer: React.FC<GeniusQuizPlayerProps> = ({
  quizId,
  quiz,
  genius: geniusProp,
  startTime,
  onPlayAgain,
  playAgainLoading = false,
  suppressResultsModal = false,
  pvpAsync,
  arenaPublishSnapshotRef,
  arenaPublishChecked: arenaPublishCheckedProp,
  onArenaPublishCheckedChange,
  arenaPublishCheckedRef: arenaPublishCheckedRefProp,
  onExitHome,
  embeddedInPvp = false,
  onPvpQuitRequest,
}) => {
  const { t } = useSafeTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const quizSoundPref = useQuizSoundPreferenceOptional();
  const genius = useMemo(() => normalizeGeniusStart(geniusProp), [geniusProp]);

  const {
    ready: wasmReady,
    error: wasmError,
    submitRound: wasmSubmitRound,
    buildFinishPayload,
  } = useGeniusWasm(genius.engineBootstrapCipher ?? undefined);

  const imageBase = quiz.geniusImageAssetsBasePath ?? '';
  const soundBase = quiz.geniusSoundAssetsBasePath ?? '';

  const panelById = useMemo(() => {
    const m = new Map<string, GeniusPanelItem>();
    for (const p of genius.panel) m.set(p.questionId, p);
    return m;
  }, [genius.panel]);

  const sequenceIds = useMemo(
    () => genius.rounds[genius.rounds.length - 1] ?? [],
    [genius.rounds]
  );

  const [roundIndex, setRoundIndex] = useState(0);
  const [phase, setPhase] = useState<GeniusPhase>('countdown');
  const [countdownLeft, setCountdownLeft] = useState(GENIUS_START_COUNTDOWN_SEC);
  const [countdownDone, setCountdownDone] = useState(false);
  const [showSoundRecommendModal, setShowSoundRecommendModal] = useState(() => !getQuizSfxEnabled());
  const [gameplayStarted, setGameplayStarted] = useState(() => getQuizSfxEnabled());
  const [soundPrefBusy, setSoundPrefBusy] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [playerPicks, setPlayerPicks] = useState<string[]>([]);
  const [wrongPickIndex, setWrongPickIndex] = useState<number | null>(null);
  const [wrongFlash, setWrongFlash] = useState(false);
  const [score, setScore] = useState(0);
  const [correctRounds, setCorrectRounds] = useState(0);
  const [roundResults, setRoundResults] = useState<SoloArenaResultLine[]>(() =>
    Array.from({ length: GENIUS_TOTAL_ROUNDS }, () => ({ pointsEarned: 0, finalAnswerText: null }))
  );

  const [saveAttemptError, setSaveAttemptError] = useState<string | null>(null);
  const [savingQuizAttempt, setSavingQuizAttempt] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultDurationSeconds, setResultDurationSeconds] = useState<number | null>(null);
  const [quizResponse, setQuizResponse] = useState<QuizAttemptResponse | null>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const [celebrationPlayerNick, setCelebrationPlayerNick] = useState('');
  const levelUpCelebration = useQuizFinishLevelUp();
  const [fallbackArenaPublishChecked, setFallbackArenaPublishChecked] = useState(true);
  const fallbackArenaPublishCheckedRef = useRef(true);
  const internalArenaPublishSnapshotRef = useRef<SoloArenaPublishSnapshot | null>(null);
  const arenaPublishSnapshotRefEffective =
    arenaPublishSnapshotRef ?? internalArenaPublishSnapshotRef;
  const arenaPublishChecked = arenaPublishCheckedProp ?? fallbackArenaPublishChecked;
  const setArenaPublishChecked = onArenaPublishCheckedChange ?? setFallbackArenaPublishChecked;
  const arenaPublishCheckedRef = arenaPublishCheckedRefProp ?? fallbackArenaPublishCheckedRef;
  const [hostedOpenSummaryForArena, setHostedOpenSummaryForArena] =
    useState<HostedOpenAsyncPvPSummary | null>(null);
  const [arenaHostedSummaryLoading, setArenaHostedSummaryLoading] = useState(false);

  const finishQuizInFlightRef = useRef(false);
  const timeoutHandledRef = useRef(false);
  const pendingTimerResetRef = useRef(false);
  const playerPicksRef = useRef<string[]>([]);
  const demoRunIdRef = useRef(0);
  const [soundPlayingTileId, setSoundPlayingTileId] = useState<string | null>(null);
  const playerClipRef = useRef<Promise<void>>(Promise.resolve());
  const wrongFeedbackShownAtRef = useRef<number | null>(null);
  const secondsToAnswerRef = useRef<number[]>(Array.from({ length: GENIUS_TOTAL_ROUNDS }, () => 0));
  const roundStartedAtRef = useRef(Date.now());

  const currentRoundIds = genius.rounds[roundIndex] ?? [];
  const expectedPickCount = currentRoundIds.length;
  const roundPoints = geniusPointsForRound(roundIndex);

  playerPicksRef.current = playerPicks;

  useEffect(() => {
    arenaPublishCheckedRef.current = arenaPublishChecked;
  }, [arenaPublishChecked, arenaPublishCheckedRef]);

  const arenaPublishOffer = useMemo(
    () =>
      !pvpAsync &&
      isQuizCompleted &&
      correctRounds / GENIUS_TOTAL_ROUNDS >= 0.7,
    [pvpAsync, isQuizCompleted, correctRounds]
  );

  const arenaPublishBlockedOpenQuiz = useMemo(() => {
    const qid = quiz?.id?.trim();
    if (!qid || !hostedOpenSummaryForArena?.openQuizIds?.length) return false;
    const lower = qid.toLowerCase();
    return hostedOpenSummaryForArena.openQuizIds.some((id) => id.trim().toLowerCase() === lower);
  }, [quiz?.id, hostedOpenSummaryForArena]);

  const arenaPublishBlockedMaxRooms = useMemo(() => {
    if (!hostedOpenSummaryForArena) return false;
    return hostedOpenSummaryForArena.openCount >= hostedOpenSummaryForArena.maxOpenRooms;
  }, [hostedOpenSummaryForArena]);

  const arenaPublishBlocked = arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms;

  const arenaPublishBlockedReason = arenaPublishBlockedOpenQuiz
    ? ('duplicateQuiz' as const)
    : arenaPublishBlockedMaxRooms
      ? ('maxRooms' as const)
      : undefined;

  useEffect(() => {
    if (arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms) {
      setArenaPublishChecked(false);
    }
  }, [arenaPublishBlockedOpenQuiz, arenaPublishBlockedMaxRooms, setArenaPublishChecked]);

  useEffect(() => {
    if (!showResultsModal || !arenaPublishOffer || !isQuizCompleted) {
      setHostedOpenSummaryForArena(null);
      setArenaHostedSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setArenaHostedSummaryLoading(true);
    void pvpService
      .getHostedOpenAsyncSummary()
      .then((s) => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(s);
          setArenaHostedSummaryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(null);
          setArenaHostedSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showResultsModal, arenaPublishOffer, isQuizCompleted]);

  const executeArenaPublishFromSoloIfNeeded = useCallback(async () => {
    const snap = arenaPublishSnapshotRefEffective.current;
    if (!snap || !arenaPublishCheckedRef.current) return;
    const qidForGate = snap.quizId?.trim();
    try {
      const summaryFresh = await pvpService.getHostedOpenAsyncSummary();
      if (summaryFresh.openCount >= summaryFresh.maxOpenRooms) return;
      if (
        qidForGate &&
        summaryFresh.openQuizIds.some((id) => id.trim().toLowerCase() === qidForGate.toLowerCase())
      )
        return;
    } catch {
      return;
    }
    try {
      const band = normalizePlayDifficultyLabel(snap.playDifficulty) || 'Medium';
      const room = await pvpService.createAsyncPvPRoom({
        name: snap.roomDisplayName.trim() || 'PvP',
        quizId: snap.quizId,
        playDifficulty: band,
      });
      await pvpService.completeAsyncHostQuiz(
        room.id,
        buildSoloArenaHostCompleteBody(snap),
        { pwaClient: isPwaStandalone() }
      );
      toast.success(t('quiz.soloArenaPublishSuccess', 'Room listed on the PvP Arena.'));
      arenaPublishSnapshotRefEffective.current = null;
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) &&
        err.response?.data &&
        typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.soloArenaPublishError', 'Could not publish to the Arena.');
      toast.error(msg);
    }
  }, [t, arenaPublishSnapshotRefEffective, arenaPublishCheckedRef]);

  const playClip = useCallback(
    async (questionId: string) => {
      const item = panelById.get(questionId);
      if (!item) return;
      const clipRun = playGeniusSoundFromKey(soundBase, item.soundAssetKey);
      playerClipRef.current = clipRun;
      await clipRun;
    },
    [panelById, soundBase]
  );

  const waitForPlayerClip = useCallback(async () => {
    await playerClipRef.current;
  }, []);

  const handleEnableQuizSounds = useCallback(async () => {
    setSoundPrefBusy(true);
    try {
      if (quizSoundPref) {
        await quizSoundPref.setSoundsEnabled(true);
      } else {
        setQuizSfxEnabledLocal(true);
      }
      setShowSoundRecommendModal(false);
      setGameplayStarted(true);
    } finally {
      setSoundPrefBusy(false);
    }
  }, [quizSoundPref]);

  const handleContinueWithoutQuizSounds = useCallback(() => {
    setShowSoundRecommendModal(false);
    setGameplayStarted(true);
  }, []);

  useEffect(() => {
    if (!showSoundRecommendModal) return;
    const enabled = quizSoundPref?.soundsEnabled ?? getQuizSfxEnabled();
    if (enabled) {
      setShowSoundRecommendModal(false);
      setGameplayStarted(true);
    }
  }, [quizSoundPref?.soundsEnabled, showSoundRecommendModal]);

  useEffect(() => {
    if (!gameplayStarted || countdownDone) return;
    if (countdownLeft <= 0) {
      setCountdownDone(true);
      return;
    }
    const timer = window.setTimeout(() => setCountdownLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdownLeft, countdownDone, gameplayStarted]);

  const runDemo = useCallback(async () => {
    await waitForPlayerClip();
    await waitForTileEffectOut();
    const runId = ++demoRunIdRef.current;
    setPhase('demo');
    setPlayerPicks([]);
    setWrongPickIndex(null);
    setWrongFlash(false);
    wrongFeedbackShownAtRef.current = null;
    setSoundPlayingTileId(null);
    setHighlightId(null);

    for (const qid of currentRoundIds) {
      if (demoRunIdRef.current !== runId) return;
      setHighlightId(qid);
      await playClip(qid);
      if (demoRunIdRef.current !== runId) return;
      setHighlightId(null);
      await new Promise((r) => setTimeout(r, DEMO_GAP_MS));
    }

    if (demoRunIdRef.current !== runId) return;
    setHighlightId(null);
    await waitForTileEffectOut();
    pendingTimerResetRef.current = true;
    setPhase('input');
  }, [currentRoundIds, playClip, waitForPlayerClip]);

  useEffect(() => {
    if (!countdownDone) return;
    void runDemo();
    return () => {
      demoRunIdRef.current += 1;
    };
  }, [roundIndex, runDemo, countdownDone]);

  const finishQuiz = useCallback(
    async (finalRoundResults: SoloArenaResultLine[]) => {
      if (finishQuizInFlightRef.current) return;
      finishQuizInFlightRef.current = true;
      setSavingQuizAttempt(true);
      const finishedAt = new Date();
      setIsQuizCompleted(true);
      const duration = Math.max(0, Math.floor((finishedAt.getTime() - startTime.getTime()) / 1000));
      setResultDurationSeconds(duration);

      const totalScore = finalRoundResults.reduce((s, r) => s + r.pointsEarned, 0);
      const totalCorrect = finalRoundResults.filter((r) => r.pointsEarned > 0).length;
      setScore(totalScore);
      setCorrectRounds(totalCorrect);

      const questionIds =
        sequenceIds.length >= GENIUS_TOTAL_ROUNDS
          ? sequenceIds.slice(0, GENIUS_TOTAL_ROUNDS)
          : Array.from(
              { length: GENIUS_TOTAL_ROUNDS },
              (_, i) => sequenceIds[i] ?? sequenceIds[sequenceIds.length - 1] ?? genius.panel[0]?.questionId ?? ''
            );

      const panelIds = genius.panel.map((p) => p.questionId);
      const responseTimes = [...secondsToAnswerRef.current];
      while (responseTimes.length < GENIUS_TOTAL_ROUNDS) responseTimes.push(0);
      const correctPerQuestion = finalRoundResults.map((r) => r.pointsEarned > 0);

      if (pvpAsync) {
        try {
          if (!wasmReady) {
            throw new Error('Genius finish audit is missing. Reload the quiz and try again.');
          }
          const geniusFinishAudit = buildFinishPayload();
          if (!geniusFinishAudit) {
            throw new Error('Genius finish audit is missing. Reload the quiz and try again.');
          }

          await pvpAsync.onComplete({
            score: totalScore,
            correctAnswers: totalCorrect,
            panelQuestionIds: panelIds,
            geniusSequenceQuestionIds: questionIds,
            responseTimeSecondsPerQuestion: responseTimes.slice(0, GENIUS_TOTAL_ROUNDS),
            correctPerQuestion,
            durationSeconds: duration,
            geniusFinishAudit,
            geniusResults: finalRoundResults,
          });
        } catch (error: unknown) {
          finishQuizInFlightRef.current = false;
          setIsQuizCompleted(false);
          const msg =
            axios.isAxiosError(error) && error.response?.data?.message
              ? String((error.response.data as { message?: string }).message)
              : error instanceof Error
                ? error.message
                : 'Unknown error';
          setSaveAttemptError(msg);
        } finally {
          setSavingQuizAttempt(false);
        }
        return;
      }

      try {
        if (!wasmReady) {
          finishQuizInFlightRef.current = false;
          setIsQuizCompleted(false);
          setSaveAttemptError('Genius finish audit is missing. Reload the quiz and try again.');
          setSavingQuizAttempt(false);
          return;
        }
        const geniusFinishAudit = buildFinishPayload();
        if (!geniusFinishAudit) {
          finishQuizInFlightRef.current = false;
          setIsQuizCompleted(false);
          setSaveAttemptError('Genius finish audit is missing. Reload the quiz and try again.');
          setSavingQuizAttempt(false);
          return;
        }

        const response = await quizService.saveQuizAttempt(
          {
            quizId,
            score: totalScore,
            correctAnswers: totalCorrect,
            totalQuestions: GENIUS_TOTAL_ROUNDS,
            duration,
            isCompleted: true,
            answers: questionIds.map(() => 'genius'),
            questionIdsInOrder: questionIds,
            responseTimeSecondsPerQuestion: responseTimes.slice(0, GENIUS_TOTAL_ROUNDS),
            witcResults: finalRoundResults,
            playDifficulty: 'Medium',
            geniusFinishAudit,
          },
          { pwaClient: isPwaStandalone() }
        );
        setQuizResponse(response);
        if (response.earnedXP !== undefined) setEarnedXP(response.earnedXP);

        if (!pvpAsync) {
          arenaPublishSnapshotRefEffective.current = null;
          if (!response.tooManySkips && totalCorrect / GENIUS_TOTAL_ROUNDS >= 0.7 && quiz) {
            arenaPublishSnapshotRefEffective.current = {
              quizId,
              playDifficulty: 'Medium',
              roomDisplayName: (quiz.title || 'Arena').trim().slice(0, 80),
              score: totalScore,
              correctAnswers: totalCorrect,
              totalQuestions: GENIUS_TOTAL_ROUNDS,
              durationSeconds: duration,
              skippedQuestions: 0,
              questionIdsInOrder: panelIds,
              answerIdsPerQuestion: panelIds.map(() => []),
              geniusSequenceQuestionIds: questionIds,
              responseTimeSecondsPerQuestion: responseTimes.slice(0, GENIUS_TOTAL_ROUNDS),
              correctPerQuestion,
              geniusFinishAudit,
              geniusResults: finalRoundResults,
            };
          }
        }
        setArenaPublishChecked(true);

        const unlocked = response.unlockedAchievements?.filter(Boolean) ?? [];
        const unlockedChars = response.unlockedCharacters?.filter(Boolean) ?? [];
        const streakUpdate = response.loginStreakUpdated ?? null;
        const hasLevelUp = levelUpCelebration.prepareFromQuizResponse(response);
        if ((unlocked.length > 0 || unlockedChars.length > 0 || streakUpdate) && !response.tooManySkips) {
          setCelebrationAchievements(unlocked);
          setCelebrationCharacters(unlockedChars);
          setCelebrationLoginStreak(streakUpdate);
          setShowCelebration(true);
        } else if (hasLevelUp && levelUpCelebration.tryOpenLevelUpCelebration()) {
          /* level-up modal */
        } else {
          setShowResultsModal(true);
        }
      } catch (error: unknown) {
        finishQuizInFlightRef.current = false;
        setIsQuizCompleted(false);
        const msg =
          axios.isAxiosError(error) && error.response?.data?.message
            ? String((error.response.data as { message?: string }).message)
            : error instanceof Error
              ? error.message
              : 'Unknown error';
        setSaveAttemptError(`Failed to save quiz results: ${msg}`);
      } finally {
        setSavingQuizAttempt(false);
      }
    },
    [
      quizId,
      sequenceIds,
      startTime,
      genius.panel,
      pvpAsync,
      arenaPublishSnapshotRefEffective,
      quiz,
      wasmReady,
      buildFinishPayload,
    ]
  );

  const endAfterRound = useCallback(
    (nextResults: SoloArenaResultLine[]) => {
      void finishQuiz(nextResults);
    },
    [finishQuiz]
  );

  const waitForWrongFeedbackDelay = useCallback(async () => {
    const shownAt = wrongFeedbackShownAtRef.current;
    if (shownAt == null) return;
    const remaining = Math.max(0, GENIUS_WRONG_RESULT_DELAY_MS - (Date.now() - shownAt));
    if (remaining > 0) await delayMs(remaining);
    wrongFeedbackShownAtRef.current = null;
  }, []);

  const submitPicks = useCallback(
    async (picks: string[], options?: { waitWrongFeedbackDelay?: boolean }) => {
      const waitWrongDelay = options?.waitWrongFeedbackDelay === true;
      if (!waitWrongDelay) setPhase('submitting');

      const elapsed = Math.min(
        GENIUS_TIME_LIMIT_SECONDS,
        Math.max(0, Math.floor((Date.now() - roundStartedAtRef.current) / 1000))
      );
      secondsToAnswerRef.current[roundIndex] = elapsed;

      try {
        if (!wasmReady) {
          throw new Error('Genius engine is not ready.');
        }

        const res = await wasmSubmitRound(picks);

        if (!res) {
          throw new Error('Genius round evaluation failed');
        }

        const nextResults = [...roundResults];
        if (res.correct) {
          playQuizSfxCorrect();
          nextResults[roundIndex] = { pointsEarned: res.pointsEarnedThisRound, finalAnswerText: null };
          setRoundResults(nextResults);
          setScore(res.totalScore);
          setCorrectRounds((c) => c + 1);

          if (res.sessionComplete) {
            setPhase('input');
            endAfterRound(nextResults);
            return;
          }

          setRoundIndex(res.nextRoundIndex);
          return;
        }

        setPhase('input');
        nextResults[roundIndex] = { pointsEarned: 0, finalAnswerText: null };
        setRoundResults(nextResults);
        setScore(res.totalScore);
        if (waitWrongDelay) await waitForWrongFeedbackDelay();
        endAfterRound(nextResults);
      } catch {
        playQuizSfxIncorrect();
        setPhase('input');
        const nextResults = [...roundResults];
        nextResults[roundIndex] = { pointsEarned: 0, finalAnswerText: null };
        setRoundResults(nextResults);
        if (waitWrongDelay) await waitForWrongFeedbackDelay();
        endAfterRound(nextResults);
      }
    },
    [roundIndex, roundResults, endAfterRound, waitForWrongFeedbackDelay, wasmReady, wasmSubmitRound]
  );

  const handleTimeout = useCallback(() => {
    if (timeoutHandledRef.current || finishQuizInFlightRef.current) return;
    if (phase !== 'input' || savingQuizAttempt || isQuizCompleted) return;

    timeoutHandledRef.current = true;
    setPhase('submitting');

    secondsToAnswerRef.current[roundIndex] = GENIUS_TIME_LIMIT_SECONDS;

    const picks = [...playerPicksRef.current];
    const nextIdx = picks.length;
    const expectedId = currentRoundIds[nextIdx];
    const wrongId =
      genius.panel.find((p) => p.questionId !== expectedId)?.questionId ??
      genius.panel[0]?.questionId ??
      '';
    if (wrongId) picks.push(wrongId);

    setWrongPickIndex(nextIdx);
    wrongFeedbackShownAtRef.current = Date.now();
    setWrongFlash(true);
    playQuizSfxIncorrect();
    window.setTimeout(() => setWrongFlash(false), 650);

    void (async () => {
      try {
        await submitPicks(picks, { waitWrongFeedbackDelay: true });
      } finally {
        timeoutHandledRef.current = false;
      }
    })();
  }, [
    phase,
    savingQuizAttempt,
    isQuizCompleted,
    roundIndex,
    currentRoundIds,
    genius.panel,
    submitPicks,
  ]);

  const timerActive =
    countdownDone &&
    phase === 'input' &&
    !isQuizCompleted &&
    !savingQuizAttempt &&
    !showQuitModal &&
    !showResultsModal &&
    !showCelebration &&
    !levelUpCelebration.showLevelUp;

  const { timeLeft, resetTimer, stopTimer } = useQuizTimer({
    timeLimit: GENIUS_TIME_LIMIT_SECONDS,
    onTimeout: handleTimeout,
    isActive: timerActive,
  });

  useEffect(() => {
    timeoutHandledRef.current = false;
  }, [roundIndex]);

  useEffect(() => {
    if (!countdownDone || isQuizCompleted) {
      stopTimer();
      return;
    }
    if (phase !== 'input') {
      stopTimer();
      return;
    }
    if (pendingTimerResetRef.current) {
      pendingTimerResetRef.current = false;
      roundStartedAtRef.current = Date.now();
      resetTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetTimer/stopTimer mudam a cada render do hook
  }, [phase, countdownDone, isQuizCompleted]);

  const handleTileClick = async (questionId: string) => {
    if (phase !== 'input' || savingQuizAttempt || isQuizCompleted || soundPlayingTileId != null) return;

    try {
      const pickIndex = playerPicks.length;
      const expectedId = currentRoundIds[pickIndex];
      const next = [...playerPicks, questionId];
      flushSync(() => {
        setPlayerPicks(next);
        setSoundPlayingTileId(questionId);
      });

      await playClip(questionId);

      if (expectedId == null || questionId !== expectedId) {
        setWrongPickIndex(pickIndex);
        wrongFeedbackShownAtRef.current = Date.now();
        setWrongFlash(true);
        playQuizSfxIncorrect();
        window.setTimeout(() => setWrongFlash(false), 650);
        setSoundPlayingTileId(null);
        await waitForTileEffectOut();
        await submitPicks(next, { waitWrongFeedbackDelay: true });
        return;
      }

      if (next.length < expectedPickCount) return;

      setSoundPlayingTileId(null);
      await waitForTileEffectOut();
      await submitPicks(next);
    } finally {
      setSoundPlayingTileId(null);
    }
  };

  const handleQuitQuiz = () => {
    if (pvpAsync && onPvpQuitRequest) {
      onPvpQuitRequest();
      return;
    }
    setShowQuitModal(true);
  };

  const confirmQuit = () => {
    setShowQuitModal(false);
    const secs = Math.max(0, Math.floor((Date.now() - startTime.getTime()) / 1000));
    setResultDurationSeconds(secs);
    setIsQuizCompleted(false);
    setShowResultsModal(true);
  };

  const handleBackToHome = async () => {
    setShowResultsModal(false);
    setShowCelebration(false);
    levelUpCelebration.clearPendingLevelUp();
    if (onExitHome) {
      void onExitHome();
      return;
    }
    await executeArenaPublishFromSoloIfNeeded();
    navigate('/', { replace: true });
  };

  const handleCelebrationFinished = () => {
    setShowCelebration(false);
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResultsModal(true);
  };

  const handleLevelUpFinished = () => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResultsModal(true);
  };

  const handlePlayAgainWithArenaPublish = () => {
    if (onExitHome) {
      void onPlayAgain();
      return;
    }
    void (async () => {
      await executeArenaPublishFromSoloIfNeeded();
      await onPlayAgain();
    })();
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCelebrationPlayerNick('');
      return;
    }
    authService
      .getProfile()
      .then((u) => setCelebrationPlayerNick((u.nickname && u.nickname.trim()) || u.name || ''))
      .catch(() => setCelebrationPlayerNick(''));
  }, []);

  const progressPercent = ((roundIndex + 1) / GENIUS_TOTAL_ROUNDS) * 100;
  const inputDisabled =
    phase !== 'input' || savingQuizAttempt || isQuizCompleted || !countdownDone || !gameplayStarted;
  const showCountdownOverlay = gameplayStarted && !countdownDone && countdownLeft > 0;

  const showRoundSubmitting =
    phase === 'submitting' && !isQuizCompleted && !savingQuizAttempt && !showResultsModal;

  const questionTitle =
    phase === 'countdown'
      ? t('genius.getReady', 'Get ready…')
      : phase === 'demo'
        ? t('genius.watchSequence', 'Watch and listen…')
        : showRoundSubmitting
          ? t('genius.checking', 'Checking…')
          : t('genius.yourTurn', 'Repeat the sequence in order');

  if (saveAttemptError) {
    return (
      <div className="quiz-error">
          <Alert variant="danger">
            <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
            <p>{saveAttemptError}</p>
            <Button variant="outline-danger" onClick={handleBackToHome}>
              {t('quiz.backToHome', 'Back to Home')}
            </Button>
          </Alert>
        </div>
    );
  }

  if (wasmError) {
    return (
      <div className="quiz-page d-flex flex-column align-items-center justify-content-center min-vh-50 text-white">
        <Alert variant="danger" className="mx-3 text-center">
          {wasmError}
        </Alert>
      </div>
    );
  }

  if (!wasmReady && !showResultsModal && !showCelebration && !levelUpCelebration.showLevelUp) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  const geniusPlayBody = (
    <>
      {showCountdownOverlay ? (
        <div
          className="genius-start-countdown-overlay"
          role="status"
          aria-live="polite"
          aria-label={t('genius.countdownAria', 'Starting in {{n}}').replace('{{n}}', String(countdownLeft))}
        >
          <span className="genius-start-countdown-digit" key={countdownLeft}>
            {countdownLeft}
          </span>
        </div>
      ) : null}
      <div className={embeddedInPvp ? 'genius-quiz-player-embedded' : 'quiz-container'}>
        <div className="quiz-header quiz-page-header">
          <QuizPlayPageHeading quiz={quiz} language={i18n.language} />
          <div className="quiz-page-header__meta d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex gap-2 align-items-center flex-wrap min-w-0 flex-grow-1">
              <Badge bg="warning">{t('quiz.medium', 'Medium')}</Badge>
              <span className="text-muted quiz-page-header__question-of">
                {t('quiz.questionOf', 'Question {{current}} of {{total}}')
                  .replace('{{current}}', String(roundIndex + 1))
                  .replace('{{total}}', String(GENIUS_TOTAL_ROUNDS))}
              </span>
              {countdownDone && phase === 'input' && !isQuizCompleted ? (
                <Badge
                  bg={timeLeft <= 10 ? 'danger' : timeLeft <= 20 ? 'warning' : 'info'}
                  className="timer-badge"
                >
                  {t('quiz.timeSeconds', '{{time}}s').replace('{{time}}', String(timeLeft))}
                </Badge>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <NavbarMusicToggle className="flex-shrink-0" />
              <QuizSoundToggleButton disabled={savingQuizAttempt} />
              <Button
                type="button"
                variant="outline-danger"
                onClick={handleQuitQuiz}
                className="quiz-quit-icon-btn flex-shrink-0"
                aria-label={t('quiz.quitQuiz', 'Quit Quiz')}
                title={t('quiz.quitQuiz', 'Quit Quiz')}
                disabled={savingQuizAttempt}
              >
                <i className="bi bi-x-lg" aria-hidden />
              </Button>
            </div>
          </div>
        </div>
        <ProgressBar
          now={progressPercent}
          className="quiz-progress-bar"
          label={`${Math.round(progressPercent)}%`}
        />

        <Card className="question-card quiz-compact mb-3">
          <Card.Body>
            <Card.Title className="question-title h5">{questionTitle}</Card.Title>
            {phase !== 'countdown' && phase !== 'submitting' ? (
              <p className="small mb-3 fw-medium text-success">
                {t('genius.roundPoints', '+{{pts}} points this round').replace(
                  '{{pts}}',
                  String(roundPoints)
                )}
              </p>
            ) : null}

            <div className="genius-play-area mb-3">
              <div className="genius-panel-grid">
                {genius.panel.map((item, slotIndex) => {
                  const urls = quizQuestionImageUrlsFromKey(imageBase, item.imageAssetKey);
                  const pickedIndex = playerPicks.lastIndexOf(item.questionId);
                  const isHighlighted = highlightId === item.questionId;
                  const isSoundPlayingTile = soundPlayingTileId === item.questionId;
                  const isPlaying = isHighlighted || isSoundPlayingTile;
                  const isPicked = pickedIndex >= 0;
                  const isWrongPick =
                    wrongPickIndex != null && playerPicks[wrongPickIndex] === item.questionId;
                  const tileGrayedWhileAnswering =
                    phase === 'input' &&
                    soundPlayingTileId != null &&
                    !isSoundPlayingTile;
                  const buttonDisabled =
                    inputDisabled || tileGrayedWhileAnswering;
                  return (
                    <button
                      key={item.questionId}
                      type="button"
                      className={[
                        'genius-panel-tile',
                        geniusPanelSlotClass(slotIndex),
                        isPlaying ? 'genius-panel-tile--playing' : '',
                        isWrongPick && wrongFlash ? 'witc-answer-input--flash' : '',
                        isPicked && !isWrongPick ? 'genius-panel-tile--picked' : '',
                        tileGrayedWhileAnswering ? 'genius-panel-tile--disabled' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      disabled={buttonDisabled}
                      onClick={() => void handleTileClick(item.questionId)}
                      aria-label={item.label || item.imageAssetKey}
                    >
                      {urls ? (
                        <img
                          src={urls.webp}
                          alt=""
                          draggable={false}
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (img.src !== urls.png) img.src = urls.png;
                          }}
                        />
                      ) : (
                        <span className="genius-panel-fallback">{item.imageAssetKey}</span>
                      )}
                      {isPicked ? (
                        <span className="genius-pick-order">{pickedIndex + 1}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>

            {wrongPickIndex != null ? (
              <div className="answer-feedback">
                <Alert variant="danger">
                  <i className="bi bi-x-circle" aria-hidden />
                  {` ${t('quiz.incorrectAnswer', 'Incorrect answer.')}`}
                </Alert>
              </div>
            ) : null}

            {showRoundSubmitting ? (
              <div className="text-center mb-3">
                <Spinner animation="border" size="sm" role="status" />{' '}
                {t('genius.checking', 'Checking…')}
              </div>
            ) : null}

            <div className="quiz-stats-actions-container">
              <div className="quiz-stats-inline">
                <div className="stat-line">
                  {t('quiz.score', 'Score')}: {score}
                </div>
                <div className="stat-line">
                  {t('quiz.correctAnswers', 'Correct')}: {correctRounds}/{GENIUS_TOTAL_ROUNDS}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {savingQuizAttempt ? (
        <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="quiz-finish-saving-overlay__inner">
            <Spinner animation="border" role="presentation" />
            <span className="text-white mt-2 small">
              {t('quiz.savingResults', 'Saving results...')}
            </span>
          </div>
        </div>
      ) : null}

      <Modal
        show={showSoundRecommendModal}
        onHide={handleContinueWithoutQuizSounds}
        centered
        backdrop="static"
        keyboard={!soundPrefBusy}
      >
        <Modal.Header closeButton={!soundPrefBusy}>
          <Modal.Title>{t('genius.soundRecommendTitle', 'Sound effects recommended')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {t(
              'genius.soundRecommendBody',
              'Genius mode uses sounds to show the sequence. We recommend enabling quiz sound effects for the best experience.'
            )}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleContinueWithoutQuizSounds}
            disabled={soundPrefBusy}
          >
            {t('genius.soundRecommendContinue', 'Continue without sound')}
          </Button>
          <Button variant="primary" onClick={() => void handleEnableQuizSounds()} disabled={soundPrefBusy}>
            {soundPrefBusy ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" role="presentation" />
                {t('genius.soundRecommendEnabling', 'Enabling…')}
              </>
            ) : (
              t('genius.soundRecommendEnable', 'Enable sound effects')
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {!pvpAsync ? (
        <SoloQuizQuitConfirmModal
          show={showQuitModal}
          onHide={() => setShowQuitModal(false)}
          onConfirm={confirmQuit}
        />
      ) : null}

      <QuizFinishCelebrationModal
        show={showCelebration}
        onFinished={handleCelebrationFinished}
        achievements={celebrationAchievements}
        characters={celebrationCharacters}
        loginStreak={celebrationLoginStreak}
        playerNickname={celebrationPlayerNick}
      />

      <LevelUpCelebrationModal
        show={levelUpCelebration.showLevelUp}
        oldLevel={levelUpCelebration.levelUpOld}
        newLevel={levelUpCelebration.levelUpNew}
        onClose={handleLevelUpFinished}
      />

      {!suppressResultsModal && (
        <SoloQuizResultsModal
          show={showResultsModal}
          hasTooManySkips={false}
          isQuizCompleted={isQuizCompleted}
          quizTitleDisplay={getQuizFullDisplayCaption(quiz, i18n.language)}
          correctAnswers={correctRounds}
          totalQuestions={GENIUS_TOTAL_ROUNDS}
          score={score}
          earnedXP={earnedXP}
          quizResponse={quizResponse}
          resultDurationSeconds={resultDurationSeconds}
          onPlayAgain={handlePlayAgainWithArenaPublish}
          onBackToHome={() => void handleBackToHome()}
          playAgainLoading={playAgainLoading}
          arenaPublishOffer={arenaPublishOffer}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishAvailabilityLoading={arenaHostedSummaryLoading}
          arenaPublishBlocked={arenaPublishBlocked}
          arenaPublishBlockedReason={arenaPublishBlockedReason}
          arenaPublishMaxOpenRooms={hostedOpenSummaryForArena?.maxOpenRooms}
        />
      )}
    </>
  );

  if (embeddedInPvp) {
    return geniusPlayBody;
  }

  return <div className="quiz-page genius-quiz-player">{geniusPlayBody}</div>;
};

export default GeniusQuizPlayer;
